import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import axios from 'axios';
import { Upload, FileText, CheckCircle, Loader2, TrendingDown, Phone, Mail, User, Zap, Flame, ArrowRight, XCircle, Camera } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PublicQuotazione = () => {
  const { code } = useParams();
  const [valid, setValid] = useState(null);
  const [agentName, setAgentName] = useState('');
  const [step, setStep] = useState('upload'); // upload | result | contact | done
  const [files, setFiles] = useState([]);  // supporta piu' bollette (luce + gas)
  const [formData, setFormData] = useState({ nome: '', cognome: '', telefono: '', email: '' });
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [leadId, setLeadId] = useState(null);

  useEffect(() => {
    const check = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/public/referral/${code}`);
        setValid(true);
        setAgentName(res.data.agente_nome);
      } catch {
        setValid(false);
      }
    };
    check();
  }, [code]);

  const handleAnalyze = async () => {
    if (files.length === 0 || !formData.nome || !formData.cognome || !formData.telefono || !privacyAccepted) return;
    setAnalyzing(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append('files', f));
      fd.append('nome', formData.nome);
      fd.append('cognome', formData.cognome);
      fd.append('telefono', formData.telefono);
      if (formData.email) fd.append('email', formData.email);
      const res = await axios.post(`${API_URL}/api/public/quotazione/${code}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000,
      });
      setResult(res.data);
      setLeadId(res.data.lead_id);
      setStep('result');
    } catch (e) {
      alert(e.response?.data?.detail || "Errore nell'analisi. Riprova.");
    } finally {
      setAnalyzing(false);
    }
  };

  const handleAddFiles = (fileList) => {
    if (!fileList) return;
    const arr = Array.from(fileList);
    setFiles((prev) => {
      const combined = [...prev, ...arr];
      // Deduplica per (nome, size) + limita a 6
      const seen = new Set();
      const uniq = [];
      for (const f of combined) {
        const key = `${f.name}:${f.size}`;
        if (!seen.has(key)) { seen.add(key); uniq.push(f); }
      }
      return uniq.slice(0, 6);
    });
  };

  const handleRemoveFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleContatto = async (vuole) => {
    try {
      await axios.post(`${API_URL}/api/public/quotazione/${code}/contatto`, {
        lead_id: leadId,
        vuole_contatto: vuole,
      });
      setStep('done');
    } catch {
      setStep('done');
    }
  };

  const handleScegliOfferta = async (offer) => {
    try {
      await axios.post(`${API_URL}/api/public/quotazione/${code}/scegli-offerta`, {
        lead_id: leadId,
        offerta_nome: offer.offerta_nome,
        compagnia: offer.compagnia,
        risparmio_stimato_annuo: offer.risparmio_stimato_annuo,
      });
      setStep('done');
    } catch (e) {
      alert(e.response?.data?.detail || "Errore nell'invio. Riprova.");
    }
  };

  const formatCurrency = (v) => {
    if (!v) return '-';
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v);
  };

  if (valid === null) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-sky-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
      </div>
    );
  }

  if (valid === false) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-sky-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="py-12 text-center">
            <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900">Link non valido</h2>
            <p className="text-slate-500 mt-2">Questo link di quotazione non è valido o è scaduto.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-sky-50 py-8 px-4" data-testid="public-quotazione-page">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-sky-600 text-white mb-4">
            <Zap className="w-7 h-7" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Quanto puoi risparmiare?</h1>
          <p className="text-slate-500 mt-1">Carica la tua bolletta e scopri subito le offerte migliori</p>
          {agentName && (
            <p className="text-sm text-sky-600 mt-2">Consulente: {agentName}</p>
          )}
        </div>

        {step === 'upload' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">I tuoi dati e la bolletta</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nome *</Label>
                  <Input
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    placeholder="Mario"
                    data-testid="pub-nome"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Cognome *</Label>
                  <Input
                    value={formData.cognome}
                    onChange={(e) => setFormData({ ...formData, cognome: e.target.value })}
                    placeholder="Rossi"
                    data-testid="pub-cognome"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Telefono *</Label>
                  <Input
                    value={formData.telefono}
                    onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                    placeholder="+39 333 1234567"
                    data-testid="pub-telefono"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email (opzionale)</Label>
                  <Input
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="mario@email.com"
                    data-testid="pub-email"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Carica le tue bollette *</Label>
                <p className="text-xs text-slate-500 -mt-1">
                  Puoi caricare piu' file insieme (es. una bolletta luce + una gas). Formati: PDF, PNG, JPG, WEBP, HEIC. Max 6 file.
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="flex-1">
                    <Input
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.heif,.gif,.bmp,.tif,.tiff,application/pdf,image/*"
                      multiple
                      onChange={(e) => { handleAddFiles(e.target.files); e.target.value = ''; }}
                      className="cursor-pointer"
                      data-testid="pub-file-input"
                    />
                  </div>
                  <Button
                    variant="outline"
                    type="button"
                    className="shrink-0 px-3"
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = 'image/*';
                      input.capture = 'environment';
                      input.multiple = true;
                      input.onchange = (e) => handleAddFiles(e.target.files);
                      input.click();
                    }}
                    data-testid="pub-camera-btn"
                    title="Scatta foto delle bollette"
                  >
                    <Camera className="w-5 h-5" />
                    <span className="ml-1.5 hidden sm:inline">Foto</span>
                  </Button>
                </div>
                {files.length > 0 && (
                  <div className="space-y-1.5 pt-1">
                    {files.map((f, idx) => (
                      <div
                        key={`${f.name}-${idx}`}
                        className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200"
                        data-testid={`pub-file-item-${idx}`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
                          <span className="text-sm text-emerald-800 truncate" title={f.name}>{f.name}</span>
                          <span className="text-[10px] text-emerald-600 shrink-0">{Math.round(f.size / 1024)} KB</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveFile(idx)}
                          className="text-red-500 hover:text-red-700 p-1 shrink-0"
                          aria-label="Rimuovi file"
                          data-testid={`pub-file-remove-${idx}`}
                        >
                          <XCircle className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <p className="text-[11px] text-slate-500 pl-1">{files.length}/6 file caricati</p>
                  </div>
                )}
              </div>
              <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
                <input
                  type="checkbox"
                  id="privacy-consent"
                  checked={privacyAccepted}
                  onChange={(e) => setPrivacyAccepted(e.target.checked)}
                  className="mt-1 w-4 h-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500 cursor-pointer"
                  data-testid="privacy-checkbox"
                />
                <label htmlFor="privacy-consent" className="text-sm text-slate-600 cursor-pointer">
                  Ho letto e accetto l'{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-sky-600 hover:underline font-medium" data-testid="privacy-link">
                    Informativa sulla Privacy
                  </a>{' '}
                  e acconsento al trattamento dei miei dati personali per ricevere un preventivo personalizzato. *
                </label>
              </div>
              <Button
                onClick={handleAnalyze}
                disabled={files.length === 0 || !formData.nome || !formData.cognome || !formData.telefono || !privacyAccepted || analyzing}
                className="w-full"
                size="lg"
                data-testid="pub-analyze-btn"
              >
                {analyzing ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Analisi in corso ({files.length} file)...
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5 mr-2" />
                    Analizza {files.length > 1 ? `le mie ${files.length} bollette` : 'la mia bolletta'}
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {step === 'result' && result && (
          <div className="space-y-4">
            <Card>
              <CardContent className="py-6 sm:py-8 text-center">
                {result.risparmia ? (
                  <>
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 mb-4">
                      <TrendingDown className="w-8 h-8 text-emerald-600" />
                    </div>
                    <h2 className="text-xl sm:text-2xl font-bold text-emerald-700 leading-tight" data-testid="pub-result-title">
                      Puoi risparmiare fino a {formatCurrency(result.risparmio_migliore)} all'anno!
                    </h2>
                    <p className="text-sm sm:text-base text-slate-600 mt-2 px-2">
                      Con l'offerta <strong>{result.offerta_migliore}</strong> di <strong>{result.compagnia_migliore}</strong>
                    </p>
                    {result.spesa_attuale > 0 && (
                      <p className="text-xs sm:text-sm text-slate-500 mt-1 px-2">
                        Spesa energia pura stimata: {formatCurrency(result.spesa_attuale)}/anno {result.fornitore_attuale ? `(${result.fornitore_attuale})` : ''}
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-100 mb-4">
                      <FileText className="w-8 h-8 text-amber-600" />
                    </div>
                    <h2 className="text-xl sm:text-2xl font-bold text-slate-800" data-testid="pub-result-title">
                      Analisi completata
                    </h2>
                    <p className="text-sm sm:text-base text-slate-600 mt-2 px-2">
                      Al momento non ci sono offerte con un risparmio significativo. Un nostro consulente potrà comunque valutare la tua situazione.
                    </p>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Top 3 offerte selezionabili */}
            {result.top_offers && result.top_offers.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-center text-sm font-semibold text-slate-700 uppercase tracking-wider">
                  Scegli l'offerta che preferisci
                </h3>
                {result.top_offers.map((offer, idx) => (
                  <Card
                    key={offer.offerta_id || idx}
                    className={`transition-all ${idx === 0 ? 'border-emerald-400 ring-2 ring-emerald-100' : 'border-slate-200'} hover:shadow-lg`}
                    data-testid={`pub-offer-card-${idx}`}
                  >
                    <CardContent className="p-4 sm:p-5">
                      <div className="flex items-start gap-3 mb-3">
                        {offer.compagnia_logo ? (
                          <img
                            src={offer.compagnia_logo}
                            alt={offer.compagnia}
                            className="w-12 h-12 rounded-lg object-contain bg-white border border-slate-200 shrink-0"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-lg bg-sky-100 text-sky-700 font-bold flex items-center justify-center shrink-0">
                            {(offer.compagnia || '?')[0]?.toUpperCase()}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-bold text-slate-900 leading-tight line-clamp-2">{offer.offerta_nome}</p>
                              <p className="text-xs text-slate-500 mt-0.5 truncate">{offer.compagnia}</p>
                            </div>
                            {idx === 0 && (
                              <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-600 text-white px-2 py-0.5 rounded-full shrink-0">Top</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-baseline justify-between mb-3 px-1">
                        <span className="text-xs text-slate-500">Risparmio annuo</span>
                        <span className="text-lg sm:text-xl font-black text-emerald-600" data-testid={`pub-offer-risparmio-${idx}`}>
                          {formatCurrency(offer.risparmio_stimato_annuo)}
                        </span>
                      </div>
                      {offer.risparmio_pct && (
                        <div className="flex items-baseline justify-between mb-3 px-1">
                          <span className="text-xs text-slate-500">Percentuale</span>
                          <span className="text-sm font-semibold text-slate-700">-{Math.round(offer.risparmio_pct)}%</span>
                        </div>
                      )}
                      <Button
                        onClick={() => handleScegliOfferta(offer)}
                        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                        size="lg"
                        data-testid={`pub-offer-select-${idx}`}
                      >
                        Voglio attivare questa
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            <Card>
              <CardContent className="py-6">
                <h3 className="text-base sm:text-lg font-semibold text-center mb-4 px-2">Preferisci essere ricontattato per una consulenza?</h3>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button
                    size="lg"
                    className="bg-sky-600 hover:bg-sky-700 sm:px-8"
                    onClick={() => handleContatto(true)}
                    data-testid="pub-contatto-si"
                  >
                    <Phone className="w-5 h-5 mr-2" />
                    Si, chiamatemi
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={() => handleContatto(false)}
                    data-testid="pub-contatto-no"
                  >
                    No, grazie
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {step === 'done' && (
          <Card>
            <CardContent className="py-12 text-center">
              <CheckCircle className="w-14 h-14 text-emerald-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-slate-900">Grazie!</h2>
              <p className="text-slate-600 mt-2">
                La tua richiesta è stata registrata. Un nostro consulente ti contatterà al più presto.
              </p>
            </CardContent>
          </Card>
        )}

        <p className="text-center text-xs text-slate-400">
          F.lli Schiliro' Servizi - Luce & Gas
        </p>
      </div>
    </div>
  );
};

export default PublicQuotazione;
